import type { Dictionary } from "./types";

/**
 * Słownik angielski. Typ `Dictionary` pochodzi ze słownika polskiego, więc pominięty
 * albo dopisany klucz jest błędem kompilacji — rozjazd języków nie ma jak dojechać
 * do produkcji (§46).
 */
export const en: Dictionary = {
  "common.appName": "VeyCue",
  "common.tagline": "Only what needs you.",
  "common.cancel": "Cancel",
  "common.save": "Save",
  "common.retry": "Try again",
  "common.loading": "Loading…",
  "common.days": { one: "{count} day", other: "{count} days" },
  "common.daysRemaining": { one: "{count} day remaining", other: "{count} days remaining" },
  "common.daysOverdue": { one: "{count} day overdue", other: "{count} days overdue" },

  "nav.today": "Today",
  "nav.cues": "Cues",
  "nav.ask": "Ask",
  "nav.documents": "Documents",
  "nav.contracts": "Contracts",
  "nav.timeline": "Timeline",
  "nav.connections": "Connections",
  "nav.company": "Company",
  "nav.settings": "Settings",
  "nav.overview": "Overview",
  "nav.attentionCenter": "Attention Center",
  "nav.more": "More",

  "today.greeting.morning": "Good morning",
  "today.greeting.afternoon": "Good afternoon",
  "today.greeting.evening": "Good evening",
  "today.attention": {
    one: "{count} thing needs your attention.",
    other: "{count} things need your attention.",
  },
  "today.allClear": "Everything else looks good.",
  "today.nothingToDo": "Nothing needs you right now.",
  "today.monitoredAreas": {
    one: "{count} monitored area is clear",
    other: "{count} monitored areas are clear",
  },

  "cue.severity.critical": "Critical",
  "cue.severity.warning": "Warning",
  "cue.severity.info": "Informational",
  "cue.status.new": "New",
  "cue.status.viewed": "Viewed",
  "cue.status.snoozed": "Snoozed",
  "cue.status.resolved": "Resolved",
  "cue.status.ignored": "Ignored",
  "cue.status.false_positive": "Wrong",
  "cue.action.resolve": "Resolve",
  "cue.action.snooze": "Snooze",
  "cue.action.ignore": "Ignore",
  "cue.action.notRelevant": "Not relevant",
  "cue.action.wrong": "This is wrong",
  "cue.whyItMatters": "Why it matters",
  "cue.recommendedAction": "Recommended action",
  "cue.source": "Source",
  "cue.viewSource": "View in document",
  "cue.action.reviewContract": "Review contract",
  "cue.contract_indexation.summary": "Indexation clause: {description}",
  "cue.contract_penalty_risk.summary": "Risk: {description}",
  "cue.obligation_due.summary": "Obligation: {description}",
  "cue.category.contracts": "Contracts",
  "cue.category.payments": "Payments",
  "cue.category.documents": "Documents",
  "cue.category.deadlines": "Deadlines",
  "cue.category.email": "Email",
  "cue.category.calendar": "Calendar",
  "cue.category.business": "Business",
  "cue.page": "Page {page}",
  "cue.confidence": "Analysis confidence: {value}%",
  "cue.empty": "No cues in this view.",

  "cue.contract_auto_renewal.title": "Contract with {counterparty} renews automatically",
  "cue.contract_notice_deadline.title": "Notice period deadline — {counterparty}",
  "cue.contract_expiry.title": "Contract with {counterparty} expires",
  "cue.contract_indexation.title": "Price indexation in {counterparty} contract",
  "cue.contract_penalty_risk.title": "Contractual penalty risk — {counterparty}",
  "cue.payment_overdue.title": "Payment overdue — {counterparty}",
  "cue.payment_due.title": "Payment due — {counterparty}",
  "cue.document_expiry.title": "Document expires",
  "cue.insurance_expiry.title": "Insurance policy ends",
  "cue.obligation_due.title": "Obligation with a deadline",
  "cue.response_requested.title": "Response expected — {counterparty}",
  "cue.customer_escalation.title": "Customer escalation — {counterparty}",
  "cue.calendar_deadline.title": "Calendar deadline",
  "cue.business_deadline.title": "Business deadline",

  "health.title": "Company Health",
  "health.score": "{score} / 100",
  "health.good": "Good",
  "health.needsAttention": "Needs attention",
  "health.poor": "Poor",
  "health.why": "Why this score",
  "health.factor.unresolved_critical": "Unresolved critical cues",
  "health.factor.overdue": "Overdue items",
  "health.factor.due_soon": "Deadlines within 7 days",
  "health.factor.stale_connections": "Connections needing re-authorisation",
  "health.factor.unmonitored_areas": "Recommended areas not monitored",

  "auth.signIn": "Sign in",
  "auth.signUp": "Create account",
  "auth.signOut": "Sign out",
  "auth.email": "Email address",
  "auth.password": "Password",
  "auth.passwordHint": "At least {min} characters. A longer phrase is safer than a short password with special characters.",
  "auth.continueWithGoogle": "Continue with Google",
  "auth.continueWithApple": "Continue with Apple",
  "auth.startTrial": "Start free trial",

  "onboarding.nip.title": "Let's start with your tax ID",
  "onboarding.nip.description": "We'll fetch public company data so you don't have to type it in.",
  "onboarding.nip.label": "Tax ID (NIP)",
  "onboarding.nip.invalid": "This is not a valid tax ID.",
  "onboarding.confirm": "Is this your company?",
  "onboarding.questionnaire.title": "A few questions about your company",
  "onboarding.questionnaire.description": "They help us show you only what actually concerns you.",

  "connections.title": "Connections",
  "connections.description": "Connect your inbox and calendar so VeyCue sees deadlines before they become problems.",
  "connections.connect": "Connect",
  "connections.disconnect": "Disconnect",
  "connections.status.active": "Active",
  "connections.status.needs_reauth": "Needs re-authorisation",
  "connections.status.error": "Sync error",
  "connections.lastSync": "Last sync: {time}",

  "documents.title": "Documents",
  "documents.upload": "Add document",
  "documents.scan": "Scan with camera",
  "documents.status.processing": "Analysing…",
  "documents.status.processed": "Analysed",
  "documents.status.failed": "Analysis failed",
  "documents.empty": "You don't have any documents yet.",

  "ask.title": "Ask VeyCue",
  "ask.placeholder": "What needs my attention this week?",
  "ask.disclaimer": "Answers are based on your company documents. VeyCue does not provide legal, accounting or tax advice.",
  "ask.aiNotice": "Answer generated by AI",

  "brief.title": "Your company this week",
  "brief.estimatedTime": "Estimated attention time: {minutes} min",

  "error.validation_failed": "Please check the values you entered.",
  "error.invalid_credentials": "Incorrect email address or password.",
  "error.unauthenticated": "Sign in to continue.",
  "error.token_expired": "Your session expired. Please sign in again.",
  "error.token_reuse_detected": "For your security we signed out all sessions. Please sign in again.",
  "error.forbidden": "You don't have permission for this action.",
  "error.insufficient_role": "This action requires higher permissions.",
  "error.not_found": "Resource not found.",
  "error.already_exists": "This resource already exists.",
  "error.rate_limited": "Too many attempts. Please try again shortly.",
  "error.entitlement_required": "This feature requires an active subscription.",
  "error.ai_budget_exceeded": "You have used the monthly AI analysis limit in your plan.",
  "billing.usageTitle": "AI analysis usage",
  "billing.usedOfBudget": "{used}% of the monthly limit used",
  "billing.budgetReached": "The analysis limit on the {from} plan has been used up. The {to} plan gives you {multiplier}× the budget — new documents will be analysed immediately.",
  "billing.trialBudgetReached": "Your trial has used its analysis allowance. Choose the {to} plan so VeyCue keeps watching your deadlines.",
  "billing.upgradeCta": "Change plan",
  "billing.degradedNotice": "New documents are still accepted and recognised, but without full AI analysis.",
  "error.internal_error": "Something went wrong. Please try again.",
  "error.offline": "No internet connection.",

  "web.hero.line1": "Your business has hundreds of things going on.",
  "web.hero.line2": "Only a few need you.",
  "web.hero.line3": "VeyCue finds them.",
  "web.hero.subtitle": "An AI-powered system that monitors your company and brings to your attention only what truly matters.",
  "web.cta.primary": "Start monitoring your business",
  "web.cta.secondary": "See how it works",
  "web.cta.trial": "14-day free trial · No credit card required",
  "web.trust.ai": "AI-powered",
  "web.trust.aiDescription": "Intelligent analysis and prioritisation",
  "web.trust.secure": "Secure by design",
  "web.trust.secureDescription": "Your data is encrypted and safe",
  "web.trust.trusted": "Trusted by businesses",
  "web.trust.trustedDescription": "Built for companies that care about focus",
  "web.pillar.focus": "Focus",
  "web.pillar.focusDescription": "Less noise, more of what matters.",
  "web.pillar.signals": "Signals",
  "web.pillar.signalsDescription": "We detect the signals inside your company.",
  "web.pillar.intelligence": "Intelligence",
  "web.pillar.intelligenceDescription": "AI analyses, understands and prioritises.",
  "web.pillar.trust": "Trust",
  "web.pillar.trustDescription": "Data security and full control.",
  "web.problem.title": "The problem isn't a lack of information",
  "web.problem.description": "Contracts renew automatically. Notice periods pass. Policies expire. Nobody is watching, because everything lives somewhere else.",
  "web.how.title": "How it works",
  "web.how.step1": "Enter your tax ID and answer a few questions.",
  "web.how.step2": "Upload contracts and connect your inbox and calendar.",
  "web.how.step3": "You only get the signals that genuinely need a decision.",
  "web.pricing.title": "Pricing",
  "web.faq.title": "Frequently asked questions",
  "web.nav.product": "Product",
  "web.nav.useCases": "Use cases",
  "web.nav.pricing": "Pricing",
  "web.nav.security": "Security",
  "web.nav.login": "Log in",
  "web.nav.trial": "Try free",
  "web.footer.privacy": "Privacy",
  "web.footer.terms": "Terms",
  "web.footer.security": "Security",
  "web.footer.contact": "Contact",
};
